import React, { useCallback, useEffect, useState } from "react";
import { Col, Label, Row } from "reactstrap";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";

import AppModal from "../../Common/AppModal";
import { getStaffLeaveCategoryListApi } from "../../../helpers/fakebackend_helper";
import {
  addStaffLeaveType,
  addStaffLeaveTypeSuccess,
  getStaffLeaveTypeList,
  updateStaffLeaveType,
  updateStaffLeaveTypeSuccess,
} from "../../../store/actions";

const DEFAULT_BG = "#EEF2FF";
const DEFAULT_TEXT = "#4F46E5";

const INITIAL = {
  Name: "",
  Description: "",
  CssColourBackground: DEFAULT_BG,
  CssColourText: DEFAULT_TEXT,
  Order: "",
  StaffLeaveCategory_Id: "",
  ContributeToHolidayAllowance: "1",
  FlagShowStaffCalendar: "1",
};

// Task 24489. Picking a category is a statement about what the leave IS;
// whether it comes off the allowance is a separate policy question the category
// does not settle (companies differ on TOIL). So the category only sets a
// SENSIBLE DEFAULT for the checkbox on a new type, and never overrides a choice
// already made on an existing one.
const DEFAULT_CONTRIBUTES_BY_CODE = {
  HOLIDAY: "1",
  SICKNESS: "0",
  UNPAID: "0",
  PARENTAL: "0",
  COMPASSIONATE: "0",
  TOIL: "0",
  TRAINING: "0",
  OTHER: "0",
};

/**
 * Task 24479, reworked after feedback that the leave types screen "feels
 * different to the other settings where we add edit and remove".
 *
 * It was: an always-visible inline form above a plain table with icon buttons.
 * Everything else in this panel is: search + Create button, a TableContainer,
 * a "..." row menu, and an AppModal popup. That was a shortcut on my part and
 * it read as a different product. This is the popup half of putting it right.
 */
export default function CreateEditStaffLeaveTypeConfig({
  modal,
  setModal,
  toggle,
  isEdit,
  updateData,
}) {
  const dispatch = useDispatch();

  const [data, setData] = useState(INITIAL);
  const [categories, setCategories] = useState([]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await getStaffLeaveCategoryListApi();
      setCategories(res?.staffleavecategory_list || []);
    } catch (e) {
      // Non-fatal - the rest of the form still saves without a category.
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    if (!isEdit) {
      setData(INITIAL);
      return;
    }
    if (isEmpty(updateData)) return;

    setData({
      Id: updateData?.Id,
      Name: updateData?.Name ?? "",
      Description: updateData?.Description ?? "",
      CssColourBackground: updateData?.CssColourBackground || DEFAULT_BG,
      CssColourText: updateData?.CssColourText || DEFAULT_TEXT,
      Order: updateData?.Order ?? "",
      StaffLeaveCategory_Id: updateData?.StaffLeaveCategory_Id ?? "",
      ContributeToHolidayAllowance: String(
        updateData?.ContributeToHolidayAllowance ?? "1"
      ),
      FlagShowStaffCalendar: String(updateData?.FlagShowStaffCalendar ?? "1"),
    });
  }, [updateData, isEdit]);

  const handleSave = () => {
    if (!data.Name.trim()) {
      toast.error("Give the leave type a name");
      return;
    }

    const payload = {
      ...data,
      Name: data.Name.trim(),
      StaffLeaveCategory_Id: data.StaffLeaveCategory_Id || null,
      // Sent as numbers because the backend stores TINYINTs; the form keeps
      // them as strings so the checkbox comparison stays a plain equality.
      ContributeToHolidayAllowance: Number(data.ContributeToHolidayAllowance),
      FlagShowStaffCalendar: Number(data.FlagShowStaffCalendar),
      Order: data.Order === "" ? null : Number(data.Order),
    };

    dispatch(isEdit ? updateStaffLeaveType([payload]) : addStaffLeaveType([payload]));
  };

  const { createSUCCESS, updateSUCCESS } = useSelector((state) => ({
    createSUCCESS: state.Staff?.addStaffLeaveTypeSuccess,
    updateSUCCESS: state.Staff?.updateStaffLeaveTypeSuccess,
  }));

  useEffect(() => {
    if (isEmpty(createSUCCESS)) return;
    if (createSUCCESS.status === 200) {
      toast.success(createSUCCESS.message);
      setModal(false);
      dispatch(getStaffLeaveTypeList());
    } else {
      toast.error(createSUCCESS.message);
    }
    dispatch(addStaffLeaveTypeSuccess([]));
  }, [createSUCCESS, dispatch, setModal]);

  useEffect(() => {
    if (isEmpty(updateSUCCESS)) return;
    if (updateSUCCESS.status === 200) {
      toast.success(updateSUCCESS.message);
      setModal(false);
      dispatch(getStaffLeaveTypeList());
    } else {
      toast.error(updateSUCCESS.message);
    }
    dispatch(updateStaffLeaveTypeSuccess([]));
  }, [updateSUCCESS, dispatch, setModal]);

  return (
    <AppModal
      open={modal}
      onClose={toggle}
      title={isEdit ? "Edit Leave Type" : "Create Leave Type"}
      icon={<BeachAccessOutlinedIcon />}
      headerAccent="indigo"
      size="sm"
      submitLabel={isEdit ? "Update" : "Create"}
      submitColor="success"
      onSubmit={handleSave}
    >
      <Row className="g-3">
        <Col lg={12}>
          <Label className="mb-1">Name</Label>
          <input
            className="form-control"
            value={data.Name}
            onChange={(e) => setData({ ...data, Name: e.target.value })}
            placeholder="e.g. Annual Leave"
          />
        </Col>

        <Col lg={12}>
          <Label className="mb-1">What kind of leave is this?</Label>
          <select
            className="form-select"
            value={data.StaffLeaveCategory_Id}
            onChange={(e) => {
              const next = e.target.value;
              const code = categories.find(
                (c) => String(c.Id) === String(next)
              )?.Code;

              setData((prev) => ({
                ...prev,
                StaffLeaveCategory_Id: next,
                // Only on a new type, and only when the checkbox has not been
                // touched away from its own default - never silently rewrite a
                // decision someone already made.
                ContributeToHolidayAllowance:
                  !isEdit && code && DEFAULT_CONTRIBUTES_BY_CODE[code]
                    ? DEFAULT_CONTRIBUTES_BY_CODE[code]
                    : prev.ContributeToHolidayAllowance,
              }));
            }}
          >
            <option value="">Choose...</option>
            {categories.map((c) => (
              <option key={c.Id} value={c.Id}>
                {c.Name}
              </option>
            ))}
          </select>
          <div className="text-muted small mt-1">
            You can call the type whatever you like - this is what the system
            treats it as, so sickness can be reported on separately from unpaid
            leave.
          </div>
        </Col>

        <Col lg={12}>
          <Label className="mb-1">Description</Label>
          <textarea
            className="form-control"
            rows="2"
            value={data.Description}
            onChange={(e) => setData({ ...data, Description: e.target.value })}
          />
        </Col>

        <Col lg={6}>
          <Label className="mb-1">Order</Label>
          <input
            type="number"
            className="form-control"
            value={data.Order}
            onChange={(e) => setData({ ...data, Order: e.target.value })}
          />
          <div className="text-muted small mt-1">
            Where it sits in pickers. Lower first.
          </div>
        </Col>

        <Col lg={6}>
          <Label className="mb-1">Colour</Label>
          <div className="d-flex align-items-center gap-2">
            {/* Live preview, because two colour swatches on their own tell you
                nothing about whether the text will be readable on the fill. */}
            <span
              className="badge"
              style={{
                background: data.CssColourBackground,
                color: data.CssColourText,
              }}
            >
              {data.Name.trim() || "Leave"}
            </span>
            <input
              type="color"
              className="form-control form-control-color"
              aria-label="Background colour"
              value={data.CssColourBackground}
              onChange={(e) =>
                setData({ ...data, CssColourBackground: e.target.value })
              }
            />
            <input
              type="color"
              className="form-control form-control-color"
              aria-label="Text colour"
              value={data.CssColourText}
              onChange={(e) =>
                setData({ ...data, CssColourText: e.target.value })
              }
            />
          </div>
        </Col>

        <Col lg={12}>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="leavetype-contributes"
              checked={data.ContributeToHolidayAllowance === "1"}
              onChange={(e) =>
                setData({
                  ...data,
                  ContributeToHolidayAllowance: e.target.checked ? "1" : "0",
                })
              }
            />
            <Label className="form-check-label" for="leavetype-contributes">
              Comes off the holiday allowance
            </Label>
            <div className="text-muted small">
              Untick for the kinds that should not eat someone&apos;s holiday -
              sickness, unpaid, compassionate.
            </div>
          </div>
        </Col>

        <Col lg={12}>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="leavetype-showcalendar"
              checked={data.FlagShowStaffCalendar === "1"}
              onChange={(e) =>
                setData({
                  ...data,
                  FlagShowStaffCalendar: e.target.checked ? "1" : "0",
                })
              }
            />
            <Label className="form-check-label" for="leavetype-showcalendar">
              Show on the staff calendar
            </Label>
          </div>
        </Col>
      </Row>
    </AppModal>
  );
}
